import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
  serializeJobSkillsForCompare,
} from '@yeshuman/sdk'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { JobSkillsManagerBody } from './employer/job-skills-manager-body'
import type { JobSkillDraft } from './employer/job-skill-manager-row'
import {
  type OrganisationOpportunity,
  useOrganisationOpportunities,
  useOpportunityMutations,
  type OrganisationOpportunitySkillData,
  managedOpportunityStructuredFields,
} from '../hooks/use-organisation-opportunities'

interface OpportunitySkillsCanvasProps {
  organisationSlug?: string
  opportunityId?: string
  opportunity: OrganisationOpportunity
  onClose: () => void
  onSaved?: (updated: OrganisationOpportunity) => void
}

export function OpportunitySkillsCanvas({
  organisationSlug = '',
  opportunityId,
  opportunity,
  onClose,
  onSaved,
}: OpportunitySkillsCanvasProps) {
  const orgMutations = useOrganisationOpportunities(organisationSlug)
  const workspaceMutations = useOpportunityMutations(opportunityId ?? opportunity.id)
  const useSlugFreeUpdate = Boolean(opportunityId)
  const isUpdating = useSlugFreeUpdate ? workspaceMutations.isUpdating : orgMutations.isUpdating
  const updateError = useSlugFreeUpdate ? workspaceMutations.updateError : orgMutations.updateError

  const didHydrateSkillsRef = useRef(false)
  const [skills, setSkills] = useState<JobSkillDraft[]>([])

  useLayoutEffect(() => {
    didHydrateSkillsRef.current = false
  }, [opportunity.id])

  useLayoutEffect(() => {
    if (didHydrateSkillsRef.current) return
    didHydrateSkillsRef.current = true
    setSkills(
      opportunity.skills.map((s) => ({
        skill_id: s.skill_id ?? s.id,
        skill_name: s.skill_name,
        requirement_type: s.requirement_type as 'required' | 'preferred',
      })),
    )
  }, [opportunity])

  const baselineSerialized = useMemo(
    () => serializeJobSkillsForCompare(opportunity.skills),
    [opportunity.skills]
  )
  const draftSerialized = useMemo(() => serializeJobSkillsForCompare(skills), [skills])
  const isDirty = baselineSerialized !== draftSerialized

  const remoteErr =
    updateError instanceof Error ? updateError.message : updateError ? String(updateError) : null

  const handleSave = () => {
    const payload: OrganisationOpportunitySkillData[] = skills.map((s) => ({
      skill_id: s.skill_id,
      requirement_type: s.requirement_type,
    }))
    const data = {
      title: opportunity.title,
      description: opportunity.description,
      skills: payload,
      ...managedOpportunityStructuredFields(opportunity),
    }
    const savePromise = useSlugFreeUpdate
      ? workspaceMutations.updateOpportunity(data)
      : orgMutations.updateOpportunity({ id: opportunity.id, data })
    void savePromise
      .then((updated) => {
        onSaved?.(updated)
        onClose()
      })
      .catch(() => {
        // Server error surfaces via updateError
      })
  }

  const slug = organisationSlug || opportunity.organisation_slug || ''

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        <JobSkillsManagerBody
          skills={skills}
          onSkillsChange={setSkills}
          organisationSlug={slug}
          disabled={isUpdating}
        />
      </div>

      <ArtifactCanvasInlineError>{remoteErr}</ArtifactCanvasInlineError>

      <ArtifactCanvasFooterBar
        footerClassName="shrink-0"
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={isUpdating}
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
